import type { CanadianTermDetails, MortgageType } from '../types/mortgageTypes';
import { calculateMonthlyRate, calculateMonthlyPayment } from './mortgageCalculations';

/** Number of renewal events before the loan is fully amortized (not including payoff). */
export function getCanadianRenewalCount(amortizationYears: number, termLengthYears: number): number {
  if (termLengthYears <= 0) return 0;
  return Math.max(0, Math.ceil(amortizationYears / termLengthYears) - 1);
}

function remainingBalanceAfterMonthlyPayments(
  principal: number,
  monthlyRate: number,
  totalPaymentsInSegment: number,
  paymentsMade: number
): number {
  if (monthlyRate === 0) {
    const payment = principal / totalPaymentsInSegment;
    return Math.max(0, principal - payment * paymentsMade);
  }
  const payment = calculateMonthlyPayment(principal, monthlyRate, totalPaymentsInSegment);
  const balance =
    principal * Math.pow(1 + monthlyRate, paymentsMade) -
    payment * ((Math.pow(1 + monthlyRate, paymentsMade) - 1) / monthlyRate);
  return Math.max(0, balance);
}

/**
 * Effective annual rate for each renewal segment (length N).
 * Missing entries inherit the previous segment rate (starting from contract rate).
 * Returns null when the renewal scenario is off, no slot has a positive rate, or inputs are incomplete.
 */
export function getEffectiveCanadianRenewalSegmentRates(
  contractRate: number,
  details: CanadianTermDetails,
  amortizationYears: number
): number[] | null {
  const N = getCanadianRenewalCount(amortizationYears, details.termLength);
  if (N <= 0 || !details.showRenewalScenario) {
    return null;
  }

  const raw: (number | undefined)[] = [];
  if (details.renewalRates != null && details.renewalRates.length > 0) {
    for (let i = 0; i < N; i++) {
      raw.push(details.renewalRates[i]);
    }
  } else if (details.renewalRate != null && Number.isFinite(details.renewalRate)) {
    for (let i = 0; i < N; i++) {
      raw.push(details.renewalRate);
    }
  } else {
    return null;
  }

  let last = contractRate;
  const effective: number[] = [];
  let hadUserRate = false;
  for (let i = 0; i < N; i++) {
    const r = raw[i];
    if (r != null && Number.isFinite(r) && r > 0) {
      last = r;
      hadUserRate = true;
    }
    effective.push(last);
  }

  if (!hadUserRate) return null;

  return effective;
}

export interface CanadianRenewalImpactRow {
  renewalIndex: number;
  year: number;
  balanceAtRenewal: number;
  paymentBeforeRenewal: number;
  paymentAfterRenewal: number;
  rateBefore: number;
  rateAfter: number;
}

/** Per-renewal payment and balance stats for the summary card (matches segment logic in the engine). */
export function computeCanadianRenewalImpactRows(
  principal: number,
  contractRate: number,
  mortgageType: MortgageType,
  details: CanadianTermDetails,
  amortYears: number
): CanadianRenewalImpactRow[] | null {
  const N = getCanadianRenewalCount(amortYears, details.termLength);
  if (N <= 0 || !details.showRenewalScenario) return null;
  const effective = getEffectiveCanadianRenewalSegmentRates(contractRate, details, amortYears);
  if (!effective) return null;

  const termMonths = details.termLength * 12;
  const totalMonths = amortYears * 12;
  let balance = principal;
  let rem = totalMonths;
  const rows: CanadianRenewalImpactRow[] = [];

  for (let seg = 0; seg < N; seg++) {
    const rateBefore = seg === 0 ? contractRate : effective[seg - 1];
    const rateAfter = effective[seg];
    const monthlyBefore = calculateMonthlyRate(rateBefore, 'Canada', mortgageType);
    const payBefore = calculateMonthlyPayment(balance, monthlyBefore, rem);
    const balanceAtRenewal = remainingBalanceAfterMonthlyPayments(
      balance,
      monthlyBefore,
      rem,
      termMonths
    );

    const remAfter = rem - termMonths;
    const monthlyAfter = calculateMonthlyRate(rateAfter, 'Canada', mortgageType);
    const payAfter =
      remAfter > 0 && balanceAtRenewal > 0
        ? calculateMonthlyPayment(balanceAtRenewal, monthlyAfter, remAfter)
        : payBefore;

    rows.push({
      renewalIndex: seg,
      year: (seg + 1) * details.termLength,
      balanceAtRenewal,
      paymentBeforeRenewal: payBefore,
      paymentAfterRenewal: payAfter,
      rateBefore,
      rateAfter,
    });

    balance = balanceAtRenewal;
    rem = remAfter;
  }

  return rows;
}

function totalInterestContractOnly(
  principal: number,
  contractRate: number,
  mortgageType: MortgageType,
  amortYears: number
): number {
  const m = calculateMonthlyRate(contractRate, 'Canada', mortgageType);
  const n = amortYears * 12;
  const pay = calculateMonthlyPayment(principal, m, n);
  return pay * n - principal;
}

/** Total interest P+I only; matches segmented renewal logic in the amortization engine (no PMI/taxes). */
export function computeCanadianRenewalTotalInterestScenario(
  principal: number,
  contractRate: number,
  mortgageType: MortgageType,
  details: CanadianTermDetails,
  amortYears: number
): { scenarioInterest: number; contractOnlyInterest: number } | null {
  const effective = getEffectiveCanadianRenewalSegmentRates(contractRate, details, amortYears);
  if (!effective) return null;

  const termMonths = details.termLength * 12;
  const totalMonths = amortYears * 12;
  const renewalCount = getCanadianRenewalCount(amortYears, details.termLength);

  let balance = principal;
  let totalInterest = 0;
  let globalRem = totalMonths;

  for (let stage = 0; stage <= renewalCount; stage++) {
    const stageEndMonthIndex = Math.min(totalMonths, (stage + 1) * termMonths);
    const stageStartMonthIndex = stage * termMonths;
    const segLen = stageEndMonthIndex - stageStartMonthIndex;
    if (segLen <= 0 || balance <= 0) break;

    const annualRate = stage === 0 ? contractRate : effective[stage - 1];
    const m = calculateMonthlyRate(annualRate, 'Canada', mortgageType);
    const p = calculateMonthlyPayment(balance, m, globalRem);
    for (let i = 0; i < segLen && balance > 0; i++) {
      const int = balance * m;
      const pr = Math.min(p - int, balance);
      totalInterest += int;
      balance -= pr;
      globalRem--;
    }
  }

  return {
    scenarioInterest: totalInterest,
    contractOnlyInterest: totalInterestContractOnly(principal, contractRate, mortgageType, amortYears),
  };
}
